import {
  Box,
  Grid,
  Paper,
  Typography,
  Stack,
  Button,
  Avatar,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Rating,
} from "@mui/material";
import DashboardHeader from "../../components/dashboard-header";
import FavoriteIcon from "@mui/icons-material/FavoriteBorder";
import MailIcon from "@mui/icons-material/NotificationsNoneOutlined";
import ThumbUpOffAltIcon from "@mui/icons-material/ThumbUpOffAlt";
import ThumbDownOffAltIcon from "@mui/icons-material/ThumbDownOffAlt";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import StarIcon from "@mui/icons-material/Star";
import AddIcon from "@mui/icons-material/Add";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import "./styles.scss";
import { useUserContext } from "../../hooks/userContext";
import { useDesignerApplicationContext } from "../../hooks/designer-application-context";
import { BasicPlanDashboard } from "../dashboard-page/basic-plan-dashboard";
import { ApplicationStatusBlock } from "../dashboard-page/application-status-block";
import { WelcomeMessage } from "../dashboard-page/welcome-message";
import { PlanKey } from "../registration/homeowner-plan-selection-fieldset";
import { DesignerApplicationSteps } from "../../__generated__/types";
import { useEffectiveRole } from "../../hooks/useEffectiveRole";
import { useGetDesignerOnboardingStatusQuery } from "../../hooks/useGetDesignerOnboardingStatusQuery";
import { useDesignerOnboardingAccess } from "../../hooks/useDesignerOnboardingAccess";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  GET_HOMEOWNER_MATCHING_PROFILE,
  GET_HOMEOWNER_STEP_STATUS,
} from "../../graphql/homeowner-matching-profile";
import { GET_HOMEOWNER_PROJECTS, UPDATE_HOMEOWNER_PROJECT } from "../../graphql/homeowner-project";
import { GET_MESSAGE_THREADS, SEND_MESSAGE } from "../../graphql/messages";
import type {
  HomeownerProjectsQueryData,
  HomeownerProjectsQueryVars,
  HomeownerProjectRecord,
} from "../homeowner-bio-page/types";
import { useGetCourseEnrollmentStatus } from "../../hooks/use-get-course-enrollment-status";
import { useGetIdeaBooks } from "../../hooks/idea-books/use-get-idea-books";
import { useNotification } from "../../contexts/NotificationContext";
import { useGetDesignerBioQuery } from "../../hooks/use-get-designer-bio";
import {
  fetchActiveCollaboration,
  confirmEndCollaboration,
  requestEndCollaboration,
  submitCollaborationFeedback,
  fetchDesignerProjects,
} from "../../services/collaborationApi";
import type {
  ActiveCollaborationResponse,
  DesignerProjectRow,
} from "../../services/collaborationApi";
import { fetchMatches, type MatchApiRecord } from "../../services/matchesApi";
import { Pages } from "../../utils";
import { formatBudgetRangeLabel, formatShortDate, toTitleCase } from "../homeowner-profile/utils";
import { BUDGET_RANGES } from "../../components/homeowner-preferences-quiz/constants";
import { PROJECT_SCOPE_OPTIONS } from "../homeowner-bio-page/data";
import { bugetRangeLabels } from "../../utils/option-helpers";
import { useNavigate } from "react-router";
import HomeownerProjectCreatePage from "../homeowner-projects-page/create-project-page";
import { DesignerWelcomePopup } from "../dashboard-page/designer-welcome-popup";

const formatEnumLabel = (value?: string | null) => {
  if (!value) return "";
  return toTitleCase(value.replace(/_/g, " ").toLowerCase());
};

const getRatingFromScore = (score?: number | null) => {
  if (typeof score !== "number" || Number.isNaN(score)) return null;
  const rating = (score / 100) * 5;
  return Math.round(rating * 10) / 10;
};

const normalizeReaction = (reaction?: string | null) => {
  if (reaction === "love_like") return "love";
  return reaction ?? null;
};

const hasLoveReaction = (reaction?: string | null) =>
  normalizeReaction(reaction) === "love";

const hasLikeReaction = (reaction?: string | null) =>
  normalizeReaction(reaction) === "like";

type ActivityItem = {
  id: string;
  name: string;
  text: string;
  avatarUrl?: string;
};

const getRecentMatchCategory = (match: MatchApiRecord) => {
  const tags = (match.topMatchTags ?? []).filter(Boolean);
  if (tags.length) {
    return tags.join(" • ");
  }
  const profileSnapshot = match.profileSnapshot as Record<string, any> | null | undefined;
  const personality = profileSnapshot?.personalityStyleResult;
  if (personality) return personality;
  const location = profileSnapshot?.location;
  if (location) return location;
  return "New match";
};

const toIdLabel = (value: unknown) => {
  if (typeof value === "string" && value.trim()) return value;
  if (typeof value === "number") return String(value);
  return null;
};

const formatDesignerMatchId = (value: string) => {
  const rawMatchId = value.trim();
  if (!rawMatchId) return "Designer";

  const prefixedAliasMatch = rawMatchId.match(/^(designer|homeowner|match)_(.+)$/i);
  if (prefixedAliasMatch) {
    return `designer_${prefixedAliasMatch[2]}`;
  }

  if (/^match_(?!h)/i.test(rawMatchId)) {
    return rawMatchId.replace(/^match_/i, "designer_");
  }

  if (/^match_h/i.test(rawMatchId)) {
    return rawMatchId.replace(/^match_h/i, "designer_");
  }

  if (/^designer_/i.test(rawMatchId)) {
    return rawMatchId.replace(/^designer_/i, "designer_");
  }

  return rawMatchId;
};

const getMatchDesignerIdLabel = (match: MatchApiRecord) => {
  const snapshot = match.profileSnapshot as Record<string, unknown> | null | undefined;
  const rawMatch = match as Record<string, unknown>;
  const primaryMatchId = toIdLabel(match.matchId);
  if (primaryMatchId) {
    return formatDesignerMatchId(primaryMatchId);
  }

  const fallbackId =
    toIdLabel(rawMatch.aliasId) ||
    toIdLabel(rawMatch.designerAliasId) ||
    toIdLabel(snapshot?.aliasId) ||
    toIdLabel(snapshot?.designerAliasId) ||
    toIdLabel(match.profileId) ||
    toIdLabel(snapshot?.profileId) ||
    toIdLabel(snapshot?.designerId) ||
    toIdLabel(rawMatch.designerId);

  if (fallbackId) {
    return formatDesignerMatchId(fallbackId);
  }

  return (
    "Designer"
  );
};

export const YourHubView = () => {
  const { user } = useUserContext();
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotification();
  const { getDesignerBio, designerBio } = useGetDesignerBioQuery();
  const {
    application,
    refreshApplicationData,
    loading: designerApplicationLoading,
  } = useDesignerApplicationContext();
  const { refreshOnboardingStatus } = useGetDesignerOnboardingStatusQuery(user?.id);
  const { isOnboardingComplete } = useDesignerOnboardingAccess();
  const { getIdeaBooks, ideaBooks } = useGetIdeaBooks();
  const [activeCollaboration, setActiveCollaboration] =
    useState<ActiveCollaborationResponse | null>(null);
  const [designerProjects, setDesignerProjects] = useState<DesignerProjectRow[]>([]);
  const [loadingDesignerProjects, setLoadingDesignerProjects] = useState(false);
  const [topMatches, setTopMatches] = useState<MatchApiRecord[]>([]);
  const [activeMatch, setActiveMatch] = useState<MatchApiRecord | null>(null);
  const [matchTotal, setMatchTotal] = useState(0);
  const [matchCounts, setMatchCounts] = useState({
    loves: 0,
    likes: 0,
    notForMe: 0,
    total: 0,
  });
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [feedbackRating, setFeedbackRating] = useState<number | null>(null);
  const [feedbackText, setFeedbackText] = useState("");
  const [platformFeedback, setPlatformFeedback] = useState("");
  const [feedbackProjectId, setFeedbackProjectId] = useState<string | null>(null);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [confirmingHomeownerCompletionId, setConfirmingHomeownerCompletionId] = useState<
    string | null
  >(null);
  const [endDialogOpen, setEndDialogOpen] = useState(false);
  const [reviewEndDialogOpen, setReviewEndDialogOpen] = useState(false);
  const [endNote, setEndNote] = useState("");
  const [requestingEnd, setRequestingEnd] = useState(false);
  const [confirmingEnd, setConfirmingEnd] = useState(false);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [projectDialogMode, setProjectDialogMode] = useState<"add" | "edit">("edit");
  const [updatingDesignerProjectId, setUpdatingDesignerProjectId] = useState<string | null>(
    null
  );
  const [projectSlideIndex, setProjectSlideIndex] = useState(0);
  
  // Check homeowner onboarding status
  const isHomeowner = user?.accountType === "Homeowner";
  const isDesigner = user?.accountType === "Designer";
  const { data: homeownerStepStatusData } = useQuery(GET_HOMEOWNER_STEP_STATUS, {
    variables: { homeOwnerId: user?.id || "" },
    skip: !isHomeowner || !user?.id,
    fetchPolicy: 'cache-and-network',
  });
  const { data: homeownerMatchingData } = useQuery(GET_HOMEOWNER_MATCHING_PROFILE, {
    variables: { homeOwnerId: user?.id || "" },
    skip: !isHomeowner || !user?.id,
    fetchPolicy: "cache-and-network",
  });
  const { data: homeownerProjectsData, refetch: refetchHomeownerProjects } = useQuery<
    HomeownerProjectsQueryData,
    HomeownerProjectsQueryVars
  >(GET_HOMEOWNER_PROJECTS, {
    variables: { homeOwnerId: user?.id || "" },
    skip: !isHomeowner || !user?.id,
    fetchPolicy: "cache-and-network",
  });
  const { data: messageThreadsData } = useQuery(GET_MESSAGE_THREADS, {
    variables: { userId: user?.id || "", role: (user?.accountType as any) || "Homeowner" },
    skip: !user?.id,
    fetchPolicy: "cache-and-network",
  });
  const [updateHomeownerProject] = useMutation(UPDATE_HOMEOWNER_PROJECT);
  const [sendMessage] = useMutation(SEND_MESSAGE);
  const { getCourseEnrollmentStatus, data: courseData } = useGetCourseEnrollmentStatus();
  
  // Check course completion for homeowners
  useEffect(() => {
    if (isHomeowner && user?.id) {
      getCourseEnrollmentStatus({
        variables: { userId: user.id }
      });
    }
  }, [isHomeowner, user?.id, getCourseEnrollmentStatus]);

  useEffect(() => {
    if (!isHomeowner || !user?.id) return;
    fetchActiveCollaboration(user.id, "homeowner")
      .then((data) => setActiveCollaboration(data))
      .catch(() => showError("Unable to load collaboration details."));
  }, [isHomeowner, user?.id, showError]);

  const loadDesignerProjects = useCallback(async () => {
    if (!isDesigner || !user?.id) return;
    setLoadingDesignerProjects(true);
    try {
      const data = await fetchDesignerProjects(user.id);
      setDesignerProjects(data.projects ?? []);
    } catch {
      showError("Unable to load designer projects.");
    } finally {
      setLoadingDesignerProjects(false);
    }
  }, [isDesigner, showError, user?.id]);

  useEffect(() => {
    loadDesignerProjects();
  }, [loadDesignerProjects]);

  useEffect(() => {
    if ((!isHomeowner && !isDesigner) || !user?.id) return;
    let cancelled = false;
    const loadMatches = async () => {
      let page = 1;
      let hasNextPage = true;
      const allMatches: MatchApiRecord[] = [];
      let lastResponseTotal = 0;

      while (hasNextPage) {
        const response = await fetchMatches(user.id, page);
        allMatches.push(...(response.matches ?? []));
        hasNextPage = Boolean(response.hasNextPage);
        lastResponseTotal = response.total ?? allMatches.length;
        page += 1;
      }

      if (cancelled) return;

      const sorted = [...allMatches].sort(
        (a, b) => (b.matchScore ?? 0) - (a.matchScore ?? 0)
      );

      setMatchTotal(lastResponseTotal || allMatches.length);
      setMatchCounts({
        total: allMatches.length,
        loves: allMatches.filter((match) => hasLoveReaction(match.reaction)).length,
        likes: allMatches.filter((match) => hasLikeReaction(match.reaction)).length,
        notForMe: allMatches.filter((match) => match.reaction === "not_for_me").length,
      });
      setTopMatches(sorted.slice(0, 3));
      setActiveMatch(sorted.find((match) => match.isMatchMade) ?? null);
    };

    loadMatches().catch(() => showError("Unable to load matches."));

    return () => {
      cancelled = true;
    };
  }, [isDesigner, isHomeowner, user?.id, showError]);

  useEffect(() => {
    if (!isHomeowner || !user?.id) return;
    getIdeaBooks(user.id, true).catch(() => {
      showError("Unable to load idea books.");
    });
  }, [getIdeaBooks, isHomeowner, showError, user?.id]);

  useEffect(() => {
    if (!isDesigner || !user?.id) return;
    fetchActiveCollaboration(user.id, "designer")
      .then((data) => setActiveCollaboration(data))
      .catch(() => showError("Unable to load collaboration details."));
  }, [isDesigner, user?.id, showError]);

  useEffect(() => {
    if (!isDesigner || !user?.id) return;
    setLoadingDesignerProjects(true);
    fetchDesignerProjects(user.id)
      .then((data) => setDesignerProjects(data.projects ?? []))
      .catch(() => showError("Unable to load designer projects."))
      .finally(() => setLoadingDesignerProjects(false));
  }, [isDesigner, user?.id, showError]);

  useEffect(() => {
    if (!isDesigner || !user?.id) return;
    getDesignerBio(user.id).catch(() => {
      showError("Unable to load designer profile.");
    });
  }, [getDesignerBio, isDesigner, showError, user?.id]);
  
  // Track if we've already refreshed to prevent infinite loops
  const hasRefreshedRef = useRef(false);
  
  // Refetch onboarding status and application data when returning to Your Hub (dashboard)
  // Only run once on mount, not on every render
  useEffect(() => {
    if (user?.accountType === 'Designer' && user?.id && !hasRefreshedRef.current) {
      hasRefreshedRef.current = true;
      
      // Refresh application data first (only if function exists)
      if (refreshApplicationData && typeof refreshApplicationData === 'function') {
        try {
          // The implementation returns a Promise, even though the type says void
          const result = refreshApplicationData() as any;
          if (result && typeof result.then === 'function') {
            result.then(() => {
            }).catch((err: any) => {
            });
          }
        } catch (err: any) {
        }
      }
      
      // Then refresh onboarding status
      if (refreshOnboardingStatus) {
        refreshOnboardingStatus(user.id).then((result: any) => {
        }).catch((err: any) => {
        });
      }
    }
    
    // Reset the ref when user changes (e.g., on logout/login)
    if (!user?.id) {
      hasRefreshedRef.current = false;
    }
  }, [user?.accountType, user?.id]); // Removed refreshApplicationData and refreshOnboardingStatus from dependencies

  // Manual refresh function for debugging
  const handleManualRefresh = () => {
    if (user?.accountType === 'Designer' && user?.id && refreshOnboardingStatus) {
      refreshOnboardingStatus(user.id).then(result => {
        // Force a page reload to ensure all components get the updated data
        window.location.reload();
      }).catch(err => {
      });
    }
  };
  
  // Debug: Log the application object to see what we're getting
  const { effectiveTier, isHomeowner: isEffectiveHomeowner } = useEffectiveRole();
  
  // Get user's subscription plan and map GraphQL enum values to PlanKey
  // Handle both object format { value: "GuildPlus" } and string format "GuildPlus"
  const rawSubscription = typeof user?.subscription === 'string' 
    ? user.subscription 
    : (user?.subscription as any)?.homeownerValue ||
      (user?.subscription as any)?.designerValue ||
      (user?.subscription as any)?.value ||
      (user?.subscription as any)?.tier ||
      'basic';
  
  // Map GraphQL enum values to PlanKey type
  const mapSubscriptionToPlanKey = (sub: string): PlanKey => {
    const normalized = String(sub ?? "")
      .trim()
      .toLowerCase()
      .replace(/[\s_-]+/g, "");

    if (!normalized || normalized === "free" || normalized === "basic") {
      return "basic";
    }
    if (
      normalized === "guildannually" ||
      normalized === "guildplus" ||
      normalized === "guildplusbeta" ||
      normalized === "guild+"
    ) {
      return "GuildPlus";
    }
    if (
      normalized === "guildmonthly" ||
      normalized === "guild" ||
      normalized === "stylefilemonthly" ||
      normalized === "stylefileannually"
    ) {
      return "Guild";
    }
    return 'basic';
  };
  
  // Use effective tier from impersonation if available, otherwise use actual subscription
  const actualUserPlan: PlanKey = mapSubscriptionToPlanKey(rawSubscription);
  const userPlan: PlanKey = effectiveTier 
    ? (effectiveTier === 'Guild+' ? 'GuildPlus' : effectiveTier === 'Basic' ? 'basic' : effectiveTier) as PlanKey
    : actualUserPlan;
  
  // Check if designer has incomplete onboarding
  // Use the proper onboarding completion status from the hook
  const hasIncompleteOnboarding = isDesigner && !isOnboardingComplete;
  const shouldShowDesignerWelcomePopup =
    hasIncompleteOnboarding &&
    !designerApplicationLoading &&
    (!application ||
      ((application.status === undefined ||
        application.status === DesignerApplicationSteps.ToBeCompleted ||
        application.status === DesignerApplicationSteps.CompleteApplication) &&
        !application.experience?.length));
  
  // Check if homeowner has incomplete onboarding (Course, Matching Profile, First Project)
  const courseCompleted =
    courseData?.getCourseEnrollmentStatus?.status === "completed" ||
    (user as any)?.courseEnrollment?.status === "completed" ||
    (user as any)?.applicationStatus === "course_completed";
  const stepStatus = homeownerStepStatusData?.getHomeownerStepStatus;
  const stepCompletion = stepStatus?.profile?.stepCompletion;
  const completedSteps = stepStatus?.completedSteps ?? [];
  const matchingProfileCompletedBySteps = completedSteps.includes("priorities");
  const matchingProfileCompleted =
    (stepCompletion
      ? stepCompletion.personalityCompleted &&
        stepCompletion.styleCompleted &&
        stepCompletion.projectCompleted &&
        stepCompletion.preferenceCompleted &&
        stepCompletion.prioritiesCompleted
      : false) || matchingProfileCompletedBySteps;
  const projectCompleted = Boolean(homeownerProjectsData?.getHomeOwnerProjects?.length);
  const hasIncompleteHomeownerOnboarding = isHomeowner && !(courseCompleted && matchingProfileCompleted && projectCompleted);

  const matchingProfile = homeownerMatchingData?.getHomeOwnerMatchingProfile ?? null;
  const preferences = matchingProfile?.preferences;
  const projectTypeLabels = useMemo(() => {
    const designItems = matchingProfile?.projectType?.designItems ?? [];
    if (!Array.isArray(designItems)) return [];
    return designItems.map((item: string) => formatEnumLabel(item)).filter(Boolean);
  }, [matchingProfile?.projectType?.designItems]);
  const projects = useMemo(() => {
    const allProjects = (homeownerProjectsData?.getHomeOwnerProjects ?? []) as HomeownerProjectRecord[];
    return allProjects.filter((project) => project?.active !== false);
  }, [homeownerProjectsData?.getHomeOwnerProjects]);
  const primaryProject = projects[0] ?? null;
  const matchingBudgetRangeLabel = useMemo(() => {
    const budget = preferences?.budgetRange;
    if (!budget) return "Not provided";
    const label = bugetRangeLabels[budget as keyof typeof bugetRangeLabels] ?? toTitleCase(budget);
    return formatBudgetRangeLabel(label);
  }, [preferences?.budgetRange]);
  const projectBudgetRangeLabel = useMemo(() => {
    if (!primaryProject?.budgetRange) return "";
    const option = BUDGET_RANGES.find(
      (range) => range.value === primaryProject.budgetRange
    );
    const label = option?.label ?? primaryProject.budgetRange;
    return formatBudgetRangeLabel(label);
  }, [primaryProject?.budgetRange]);
  const budgetRangeLabel = projectBudgetRangeLabel || matchingBudgetRangeLabel;
  const projectScopeSummary = useMemo(() => {
    if (!primaryProject?.projectScope?.length) {
      return projectTypeLabels.slice(0, 3).join(", ");
    }
    const labels = primaryProject.projectScope
      .map((value: string) => {
        const option = PROJECT_SCOPE_OPTIONS.find(
          (item) => item.value === value || item.label === value
        );
        return option?.label ?? formatEnumLabel(value);
      })
      .filter(Boolean);
    return labels.slice(0, 3).join(", ");
  }, [primaryProject?.projectScope, projectTypeLabels]);
  const messageCount = useMemo(() => {
    const threads = messageThreadsData?.getMessageThreads ?? [];
    if (!threads.length) return 0;
    const totalUnread = threads.reduce(
      (sum: number, thread: any) => sum + (thread?.unreadCount ?? 0),
      0
    );
    return totalUnread || threads.length;
  }, [messageThreadsData?.getMessageThreads]);
  const hasActiveCollaboration = Boolean(activeCollaboration?.collaboration);
  const activeDesigner = activeCollaboration?.designer ?? null;
  const activeHomeowner = activeCollaboration?.homeowner ?? null;
  const activeDesignerRating =
    getRatingFromScore(activeMatch?.matchScore ?? null) ??
    (typeof activeDesigner?.averageRating === "number"
      ? Math.round(activeDesigner.averageRating * 10) / 10
      : null);
  const projectTypeSummary = projectScopeSummary;
  const shouldShowEndRequestBanner =
    isDesigner &&
    activeCollaboration?.collaboration?.status === "end_requested" &&
    activeCollaboration?.collaboration?.endRequestedBy === "homeowner";
  const endRequestHomeownerName = activeHomeowner?.name?.trim() || "Homeowner";
  const endRequestNote =
    activeCollaboration?.collaboration?.endNote?.trim() || "No note provided.";

  const refreshActiveCollaboration = useCallback(() => {
    if (!user?.id || (!isHomeowner && !isDesigner)) return;
    const role = isDesigner ? "designer" : "homeowner";
    fetchActiveCollaboration(user.id, role)
      .then((data) => setActiveCollaboration(data))
      .catch(() => showError("Unable to refresh collaboration details."));
  }, [isDesigner, isHomeowner, showError, user?.id]);

  const handleNavigateMatches = useCallback(() => {
    navigate(Pages.MATCHES);
  }, [navigate]);

  const handleNavigateMatchesWithFilter = useCallback(
    (filterKey: "loves" | "likes" | "not_for_me") => {
      navigate(`${Pages.MATCHES}?filter=${filterKey}`);
    },
    [navigate]
  );

  const handleNavigateMessages = useCallback(() => {
    navigate(Pages.MESSAGES);
  }, [navigate]);

  const handleViewDesignerProfile = useCallback(
    (designerId?: string | null, match?: MatchApiRecord | null) => {
      if (!designerId) return;

      const resolveMatch = () => {
        if (match) return match;
        const candidates: Array<MatchApiRecord | null> = [activeMatch, ...topMatches];
        for (const candidate of candidates) {
          if (!candidate) continue;
          const snapshot = candidate.profileSnapshot as
            | Record<string, unknown>
            | null
            | undefined;
          const candidateIds = [
            candidate.profileId,
            candidate.matchId,
            snapshot?.profileId,
            snapshot?.designerId,
            snapshot?.matchId,
          ].filter((value): value is string => typeof value === "string");
          if (candidateIds.includes(designerId)) {
            return candidate;
          }
        }
        return null;
      };

      const resolvedMatch = resolveMatch();
      const topMatchTags = (resolvedMatch?.topMatchTags ?? [])
        .filter((tag) => typeof tag === "string" && tag.trim())
        .slice(0, 3);
      const profileState =
        resolvedMatch || topMatchTags.length
          ? {
              matchScore: resolvedMatch?.matchScore ?? null,
              ...(topMatchTags.length ? { topMatchTags } : {}),
            }
          : null;

      const profileRoute = Pages.DESIGNER_PREMIUM_PROFILE.replace(":designerId", designerId);
      if (profileState) {
        navigate(profileRoute, { state: profileState });
      } else {
        navigate(profileRoute);
      }
    },
    [activeMatch, navigate, topMatches]
  );

  const handleOpenFeedback = (projectId?: string | null) => {
    setFeedbackProjectId(projectId ?? null);
    setFeedbackDialogOpen(true);
  };

  const handleOpenProjectDialog = useCallback((mode: "add" | "edit") => {
    setProjectDialogMode(mode);
    setProjectDialogOpen(true);
  }, []);

  const handleCloseProjectDialog = useCallback(() => {
    setProjectDialogOpen(false);
  }, []);

  const handleProjectDialogComplete = useCallback(() => {
    setProjectDialogOpen(false);
    if (isHomeowner && user?.id) {
      void refetchHomeownerProjects();
    }
  }, [isHomeowner, refetchHomeownerProjects, user?.id]);

  const handleSubmitFeedback = async () => {
    if (!user?.id) return;
    if (!feedbackRating) {
      showError("Please provide a rating before submitting.");
      return;
    }
    if (!activeCollaboration?.collaboration?._id) {
      showError("Unable to request completion without an active collaboration.");
      return;
    }

    const projectId = feedbackProjectId ?? primaryProject?._id ?? null;
    if (!projectId) {
      showError("Unable to locate the project to request completion.");
      return;
    }

    setSubmittingFeedback(true);
    try {
      await updateHomeownerProject({
        variables: { _id: projectId, completionRequestedBy: "homeowner" },
      });

      const completedProject =
        projects.find((project) => project._id === projectId) ?? null;
      const projectLabel = completedProject?.projectName?.trim();
      const designerId = activeCollaboration?.designer?._id;
      if (designerId) {
        await sendMessage({
          variables: {
            input: {
              senderId: user.id,
              senderRole: "Homeowner",
              recipientId: designerId,
              recipientRole: "Designer",
              body: `${user.firstName || "Homeowner"} requested to mark ${
                projectLabel ? `"${projectLabel}"` : "your project"
              } as complete. Please confirm when you're ready.`,
            },
          },
        });
      }

      await submitCollaborationFeedback(activeCollaboration.collaboration._id, user.id, {
        projectId,
        rating: feedbackRating,
        review: feedbackText,
        platformFeedback,
      });
      showSuccess("Completion request sent. Feedback submitted.");
      setFeedbackDialogOpen(false);
      setFeedbackText("");
      setPlatformFeedback("");
      setFeedbackRating(null);
      setFeedbackProjectId(null);
      void refetchHomeownerProjects();
    } catch {
      showError("Unable to request completion. Please try again.");
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const handleHomeownerConfirmCompletion = async (projectId: string) => {
    if (!projectId) return;
    setConfirmingHomeownerCompletionId(projectId);
    try {
      await updateHomeownerProject({
        variables: {
          _id: projectId,
          status: "completed",
          completionConfirmedBy: "homeowner",
        },
      });
      showSuccess("Project marked complete.");
      void refetchHomeownerProjects();
    } catch {
      showError("Unable to complete the project. Please try again.");
    } finally {
      setConfirmingHomeownerCompletionId(null);
    }
  };

  const projectCards = useMemo(() => {
    if (!projects.length) return [];
    const cards = projects.map((project, index) => {
      const projectScopeLabel = project.projectScope?.length
        ? project.projectScope
            .map((value: string) => {
              const option = PROJECT_SCOPE_OPTIONS.find(
                (item) => item.value === value || item.label === value
              );
              return option?.label ?? formatEnumLabel(value);
            })
            .filter(Boolean)
            .slice(0, 3)
            .join(", ")
        : projectTypeSummary;
      const projectBudgetLabel = project.budgetRange
        ? formatBudgetRangeLabel(
            BUDGET_RANGES.find((range) => range.value === project.budgetRange)?.label ??
              project.budgetRange
          )
        : budgetRangeLabel;
      const completionPending =
        Boolean(project.completionRequestedBy) &&
        !project.completionConfirmedAt &&
        project.status !== "completed";
      const pendingByDesigner = project.completionRequestedBy === "designer";
      const pendingByHomeowner =
        completionPending && project.completionRequestedBy === "homeowner";

      return (
        <div
          key={project._id ?? project.projectName ?? `project-${index}`}
          className="hub-project-card"
        >
          {completionPending ? (
            <div className="hub-project-complete-requested">Complete Requested</div>
          ) : null}
          <div className="hub-project-title">{project.projectName || "Untitled Project"}</div>
          <div className="hub-project-meta-block">
            <div className="hub-project-meta">
              <span>Project Type:</span>
              <span>{projectScopeLabel || "Not provided"}</span>
            </div>
            <div className="hub-project-meta">
              <span>Budget Range:</span>
              <span>{projectBudgetLabel}</span>
            </div>
          </div>
          <div className="hub-project-actions">
            <Button
              variant="outlined"
              className="hub-button-outline"
              onClick={() => navigate(Pages.MY_BIO)}
            >
              View Notes
            </Button>
            {completionPending && pendingByDesigner ? (
              <Button
                variant="contained"
                className="hub-button-primary"
                onClick={() =>
                  project._id && handleHomeownerConfirmCompletion(project._id)
                }
                disabled={confirmingHomeownerCompletionId === project._id}
              >
                {confirmingHomeownerCompletionId === project._id
                  ? "Updating..."
                  : "Mark as Complete"}
              </Button>
            ) : completionPending && pendingByHomeowner ? (
              <Button variant="contained" className="hub-button-primary" disabled>
                Pending Approval
              </Button>
            ) : (
              <Button
                variant="contained"
                className="hub-button-primary"
                onClick={() => handleOpenFeedback(project._id)}
              >
                Mark Project as Complete
              </Button>
            )}
          </div>
        </div>
      );
    });

    if (projects.length === 1) {
      cards.push(
        <div key="project-placeholder" className="hub-project-card hub-project-card-placeholder">
          <div className="hub-project-placeholder-icon">
            <AddIcon fontSize="small" />
          </div>
          <div className="hub-project-placeholder-title">Expand Your Journey</div>
          <div className="hub-project-placeholder-text">
            by adding another room or renovation.
          </div>
          <Button
            variant="contained"
            className="hub-button-primary hub-project-placeholder-button"
            onClick={() => handleOpenProjectDialog("add")}
          >
            <AddIcon fontSize="small" />
            Start a New Project
          </Button>
        </div>
      );
    }

    return cards;
  }, [
    budgetRangeLabel,
    confirmingHomeownerCompletionId,
    handleHomeownerConfirmCompletion,
    handleOpenFeedback,
    handleOpenProjectDialog,
    navigate,
    projectTypeSummary,
    projects,
  ]);

  const projectSlides = useMemo(() => {
    if (!projectCards.length) return [];
    const slides: React.ReactNode[][] = [];
    for (let index = 0; index < projectCards.length; index += 2) {
      slides.push(projectCards.slice(index, index + 2));
    }
    return slides;
  }, [projectCards]);

  const hasProjectSlider = projectSlides.length > 1;
  const projectSlideGap = 16;

  useEffect(() => {
    if (!projectSlides.length) {
      setProjectSlideIndex(0);
      return;
    }
    setProjectSlideIndex((prev) =>
      Math.min(prev, Math.max(projectSlides.length - 1, 0))
    );
  }, [projectSlides.length]);

  const handleRequestEndCollaboration = async () => {
    if (!user?.id || !activeCollaboration?.collaboration?._id) return;
    setRequestingEnd(true);
    try {
      await requestEndCollaboration(
        activeCollaboration.collaboration._id,
        user.id,
        endNote
      );
      showSuccess("End collaboration request sent.");
      setEndDialogOpen(false);
      setEndNote("");
      refreshActiveCollaboration();
    } catch {
      showError("Unable to request end collaboration.");
    } finally {
      setRequestingEnd(false);
    }
  };

  const handleConfirmEndCollaboration = async () => {
    if (!user?.id || !activeCollaboration?.collaboration?._id) return;
    setConfirmingEnd(true);
    try {
      await confirmEndCollaboration(activeCollaboration.collaboration._id, user.id);
      showSuccess("Collaboration ended.");
      setReviewEndDialogOpen(false);
      refreshActiveCollaboration();
    } catch {
      showError("Unable to confirm end collaboration.");
    } finally {
      setConfirmingEnd(false);
    }
  };

  const handleDesignerStartProject = async (projectId: string) => {
    if (!projectId) return;
    setUpdatingDesignerProjectId(projectId);
    try {
      await updateHomeownerProject({
        variables: { _id: projectId, status: "in_progress" },
      });
      showSuccess("Project started.");
      setDesignerProjects((prev) =>
        prev.map((project) =>
          project.projectId === projectId
            ? { ...project, status: "in_progress" }
            : project
        )
      );
      await loadDesignerProjects();
    } catch {
      showError("Unable to start the project.");
    } finally {
      setUpdatingDesignerProjectId(null);
    }
  };

  const handleDesignerRequestCompletion = async (projectId: string) => {
    if (!projectId) return;
    setUpdatingDesignerProjectId(projectId);
    try {
      await updateHomeownerProject({
        variables: { _id: projectId, completionRequestedBy: "designer" },
      });
      showSuccess("Completion request sent to homeowner.");
      setDesignerProjects((prev) =>
        prev.map((project) =>
          project.projectId === projectId
            ? {
                ...project,
                completionRequestedBy: "designer",
                completionRequestedAt: new Date().toISOString(),
              }
            : project
        )
      );
      await loadDesignerProjects();
    } catch {
      showError("Unable to request completion.");
    } finally {
      setUpdatingDesignerProjectId(null);
    }
  };

  const handleDesignerConfirmCompletion = async (projectId: string) => {
    if (!projectId) return;
    setUpdatingDesignerProjectId(projectId);
    try {
      await updateHomeownerProject({
        variables: { _id: projectId, completionConfirmedBy: "designer" },
      });
      showSuccess("Project marked complete.");
      setDesignerProjects((prev) =>
        prev.map((project) =>
          project.projectId === projectId
            ? {
                ...project,
                status: "completed",
                completionConfirmedBy: "designer",
                completionConfirmedAt: new Date().toISOString(),
              }
            : project
        )
      );
      await loadDesignerProjects();
    } catch {
      showError("Unable to complete the project.");
    } finally {
      setUpdatingDesignerProjectId(null);
    }
  };

  const getDesignerStatusLabel = (status: DesignerProjectRow["status"]) => {
    switch (status) {
      case "not_started":
        return "Not Started";
      case "completed":
        return "Complete";
      case "cancelled":
        return "Cancelled";
      case "in_progress":
      default:
        return "In Progress";
    }
  };

  const homeownerMetrics = useMemo(
    () => [
      {
        label: "Messages",
        value: messageCount,
        button: "Inbox",
        icon: <MailIcon />,
        onClick: handleNavigateMessages,
      },
      {
        label: "Loves",
        value: matchCounts.loves,
        button: "Connect",
        icon: <FavoriteIcon />,
        onClick: () => handleNavigateMatchesWithFilter("loves"),
      },
      {
        label: "Not a Fit",
        value: matchCounts.notForMe,
        button: "See Feedback",
        icon: <ThumbDownOffAltIcon />,
        onClick: () => handleNavigateMatchesWithFilter("not_for_me"),
      },
      {
        label: "Likes",
        value: matchCounts.likes,
        button: "Review",
        icon: <ThumbUpOffAltIcon />,
        onClick: () => handleNavigateMatchesWithFilter("likes"),
      },
    ],
    [
      handleNavigateMatches,
      handleNavigateMatchesWithFilter,
      handleNavigateMessages,
      matchCounts.likes,
      matchCounts.loves,
      matchCounts.notForMe,
      messageCount,
    ]
  );

  const matchesWon = useMemo(() => {
    if (!designerProjects.length) return 0;
    return designerProjects.filter((project) => project.status !== "cancelled").length;
  }, [designerProjects]);

  const designerAverageRating = useMemo(() => {
    const reviews = designerBio?.reviews ?? [];
    if (reviews.length) {
      const total = reviews.reduce(
        (sum: number, review: any) => sum + (review?.rating ?? 0),
        0
      );
      const average = total / reviews.length;
      return Math.round(average * 10) / 10;
    }
    if (typeof activeCollaboration?.designer?.averageRating === "number") {
      return Math.round(activeCollaboration.designer.averageRating * 10) / 10;
    }
    return null;
  }, [activeCollaboration?.designer?.averageRating, designerBio?.reviews]);

  const designerMetrics = useMemo(
    () => [
      {
        label: "Messages",
        value: messageCount,
        button: "Inbox",
        icon: <MailIcon />,
        onClick: handleNavigateMessages,
      },
      {
        label: "Loves",
        value: matchCounts.loves,
        button: "Connect",
        icon: <FavoriteIcon />,
        onClick: () => handleNavigateMatchesWithFilter("loves"),
      },
      {
        label: "Not a Fit",
        value: matchCounts.notForMe,
        button: "See Feedback",
        icon: <ThumbDownOffAltIcon />,
        onClick: () => handleNavigateMatchesWithFilter("not_for_me"),
      },
      {
        label: "Likes",
        value: matchCounts.likes,
        button: "Review",
        icon: <ThumbUpOffAltIcon />,
        onClick: () => handleNavigateMatchesWithFilter("likes"),
      },
      {
        label: "Matches Won",
        value: matchesWon,
        icon: <EmojiEventsIcon />,
        wide: true,
      },
      {
        label: "Average Rating",
        value: designerAverageRating,
        rating: true,
        icon: <StarIcon />,
        wide: true,
      },
    ],
    [
      designerAverageRating,
      handleNavigateMatches,
      handleNavigateMatchesWithFilter,
      handleNavigateMessages,
      matchCounts.likes,
      matchCounts.loves,
      matchCounts.notForMe,
      matchesWon,
      messageCount,
    ]
  );

  const feedbackDesignerName = activeDesigner?.name || "your designer";
  const isPositiveFeedback = feedbackRating === null ? true : feedbackRating >= 4;
  const feedbackMessage = isPositiveFeedback
    ? "Thank you for rating! Could you please leave a review."
    : "Thank you for rating!";
  const feedbackSubmitLabel = isPositiveFeedback ? "Submit Review" : "Submit Feedback";

  const activityItems = useMemo<ActivityItem[]>(() => {
    const threads = messageThreadsData?.getMessageThreads ?? [];
    if (threads.length) {
      return threads.slice(0, 4).map((thread: any) => {
        const otherParticipant = (thread.participants ?? []).find(
          (participant: any) => participant.userId !== user?.id
        );
        return {
          id: thread._id,
          name: otherParticipant?.displayName ?? "New message",
          text: thread.lastMessage?.body ?? "New message",
          avatarUrl: otherParticipant?.avatarUrl ?? "",
        };
      });
    }
    if (isDesigner) {
      return [
        {
          id: "activity-1",
          name: activeHomeowner?.name || "Homeowner",
          text: activeHomeowner?.name
            ? `You're working with ${activeHomeowner.name}`
            : "You're working with a homeowner",
          avatarUrl: "",
        },
        {
          id: "activity-2",
          name: "New match",
          text: matchTotal ? `${matchTotal} active matches available` : "New match ready to review",
        },
      ];
    }
    return [
      {
        id: "activity-1",
        name: activeDesigner?.name || "Your designer",
        text: "You're working with your designer",
        avatarUrl: activeDesigner?.profilePicUrl ?? "",
      },
      {
        id: "activity-2",
        name: primaryProject?.projectName || "Project",
        text: "Matching started for your project",
      },
    ];
  }, [
    activeDesigner?.name,
    activeDesigner?.profilePicUrl,
    activeHomeowner?.name,
    isDesigner,
    matchTotal,
    messageThreadsData?.getMessageThreads,
    primaryProject?.projectName,
    user?.id,
  ]);

  const designerRecentMatches = useMemo(() => {
    return topMatches.slice(0, 3).map((match) => ({
      id: match.profileId ?? match.matchId,
      name: match.name ?? match.matchId,
      category: getRecentMatchCategory(match),
      status: match.isOnline ? "Online" : "Offline",
      avatar: match.profilePicUrl ?? "",
    }));
  }, [topMatches]);

  const onboardingDeviceNotice = (
    <Paper elevation={0} className="hub-onboarding-notice">
      <Typography className="hub-onboarding-notice-title">Please Note:</Typography>
      <Typography className="hub-onboarding-notice-text">
        While the site is mobile device friendly, the onboarding experience may be more
        intuitive from a device with a larger screen.
      </Typography>
      <Typography className="hub-onboarding-notice-text">
        <strong>Important:</strong> If using a mobile device for onboarding, you will need to
        enable pop-up windows. To enable, go to settings on your mobile device and find
        Block Pop-ups, Toggle it OFF.
      </Typography>
    </Paper>
  );

  
  
  // For Basic and Guild plan homeowners (or impersonating), show the limited dashboard
  if (isEffectiveHomeowner && (userPlan === 'basic' || userPlan === 'Guild')) {
    return <BasicPlanDashboard user={user} planKey={userPlan} />;
  }
  
  // For designers with incomplete onboarding, show onboarding steps
  if (hasIncompleteOnboarding) {
    return (
      <Box>
        {shouldShowDesignerWelcomePopup && <DesignerWelcomePopup />}
        {onboardingDeviceNotice}
        <WelcomeMessage
          firstName={user?.firstName ?? ""}
          status={application?.status ?? undefined}
        />
        <ApplicationStatusBlock
          currentStatus={application?.status ?? DesignerApplicationSteps.CompleteApplication}
          accountType="designer"
        />
      </Box>
    );
  }
  
  // For homeowners with incomplete onboarding, show onboarding steps
  if (hasIncompleteHomeownerOnboarding) {
    return (
      <Box>
        {onboardingDeviceNotice}
        <WelcomeMessage
          firstName={user?.firstName ?? ""}
          status={undefined}
        />
        <ApplicationStatusBlock
          currentStatus={undefined}
          accountType="homeowner"
        />
      </Box>
    );
  }
  
  if (isEffectiveHomeowner) {
    return (
      <Box className="your-hub homeowner-hub">
        <div className="hub-header">
          <Typography variant="h4" className="hub-title">
            <span className="hub-title-prefix">Welcome Back</span>
            {user?.firstName ? (
              <span className="hub-title-name">, {user.firstName}</span>
            ) : null}
          </Typography>
        </div>

        <div className="hub-metrics-grid">
          {homeownerMetrics.map((metric) => (
            <Paper key={metric.label} className="hub-metric-card" elevation={0}>
              <div className="hub-metric-row">
                <div className="hub-metric-left">
                  <div className="hub-metric-icon">{metric.icon}</div>
                  <div className="hub-metric-label">{metric.label}</div>
                </div>
                <div className="hub-metric-value">
                  {String(metric.value ?? 0).padStart(2, "0")}
                </div>
              </div>
              <Button
                className="hub-metric-action"
                variant="outlined"
                size="small"
                onClick={metric.onClick}
              >
                {metric.button}
              </Button>
            </Paper>
          ))}
        </div>

        <div className="hub-main-grid">
          <div className="hub-main-left">
            <Paper className="hub-card hub-projects-card" elevation={0}>
              <div className="hub-card-header">
                <Typography variant="h6" fontWeight={700}>
                  My Project(s)
                </Typography>
                {hasActiveCollaboration && (
                  <Button
                    variant="text"
                    className="hub-link-button"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenProjectDialog("add")}
                  >
                    Add New Project
                  </Button>
                )}
              </div>

              {hasActiveCollaboration ? (
                <>
                  {!projectCards.length ? (
                    <div className="hub-empty">No active projects yet.</div>
                  ) : hasProjectSlider ? (
                    <div className="hub-project-slider">
                      <div className="hub-project-slider-viewport">
                        <div
                          className="hub-project-slider-track"
                          style={{
                            transform: `translateX(calc(-${projectSlideIndex} * (100% + ${projectSlideGap}px)))`,
                          }}
                        >
                          {projectSlides.map((slide, index) => (
                            <div className="hub-project-slide" key={`slide-${index}`}>
                              <div className="hub-project-grid hub-project-grid--slider">
                                {slide}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="hub-project-slider-controls">
                        <IconButton
                          size="small"
                          onClick={() =>
                            setProjectSlideIndex((prev) => Math.max(prev - 1, 0))
                          }
                          disabled={projectSlideIndex === 0}
                        >
                          <ChevronLeftIcon fontSize="small" />
                        </IconButton>
                        <div className="hub-project-slider-indicator">
                          {projectSlideIndex + 1} / {projectSlides.length}
                        </div>
                        <IconButton
                          size="small"
                          onClick={() =>
                            setProjectSlideIndex((prev) =>
                              Math.min(prev + 1, projectSlides.length - 1)
                            )
                          }
                          disabled={projectSlideIndex >= projectSlides.length - 1}
                        >
                          <ChevronRightIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </div>
                  ) : (
                    <div className="hub-project-grid">{projectCards}</div>
                  )}
                </>
              ) : (
                <div className="hub-project-progress">
                  <div className="hub-project-progress-header">
                    <Typography variant="subtitle1" fontWeight={700}>
                      {matchTotal || 0} Potential Matches Found
                    </Typography>
                    <span className="hub-status-chip">Matching In Progress</span>
                  </div>
                  <Typography variant="body2" className="hub-muted">
                    Our Smart-Guild algorithm is actively evaluating designers who meet your
                    project needs.
                  </Typography>
                  <div className="hub-project-progress-details-card">
                    <div className="hub-project-progress-details">
                      <div>
                        <span>Project Name:</span>
                        <span>{primaryProject?.projectName || "Your Project"}</span>
                      </div>
                      <div>
                        <span>Project Type:</span>
                        <span>{projectTypeSummary || "Not provided"}</span>
                      </div>
                      <div>
                        <span>Budget Range:</span>
                        <span>{budgetRangeLabel}</span>
                      </div>
                    </div>
                  </div>
                  <div className="hub-project-progress-actions">
                    <Button
                      variant="outlined"
                      className="hub-button-outline"
                      onClick={() => handleOpenProjectDialog("edit")}
                    >
                      Review/Edit Project
                    </Button>
                    <Button
                      variant="contained"
                      className="hub-button-primary"
                      onClick={handleNavigateMatches}
                    >
                      View Matches
                    </Button>
                  </div>
                </div>
              )}
            </Paper>

            <Paper className="hub-card hub-idea-books-card" elevation={0}>
              <div className="hub-card-header">
                <Typography variant="h6" fontWeight={700}>
                  Saved Idea Books
                </Typography>
                <Button
                  variant="text"
                  className="hub-link-button"
                  startIcon={<AddIcon />}
                  onClick={() => navigate(Pages.MY_IDEA_BOOKS)}
                >
                  Create New Idea Book
                </Button>
              </div>
              <div className="hub-idea-books-grid">
                {ideaBooks.length ? (
                  ideaBooks.slice(0, 5).map((book) => (
                    <div
                      key={book._id}
                      className="hub-idea-book"
                      role="button"
                      tabIndex={0}
                      onClick={() =>
                        navigate(
                          Pages.MY_IDEA_BOOK_DETAILS.replace(
                            ":ideaBookId",
                            book._id
                          )
                        )
                      }
                      onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                          event.preventDefault();
                          navigate(
                            Pages.MY_IDEA_BOOK_DETAILS.replace(
                              ":ideaBookId",
                              book._id
                            )
                          );
                        }
                      }}
                    >
                      <div className="hub-idea-book-cover">
                        {book.coverImage?.url ? (
                          <img src={book.coverImage.url} alt={book.name} />
                        ) : (
                          <div className="hub-idea-book-placeholder" />
                        )}
                      </div>
                      <div className="hub-idea-book-title">{book.name}</div>
                      <div className="hub-idea-book-meta">
                        {book.updatedAt
                          ? `Last modified ${new Date(book.updatedAt).toLocaleDateString()}`
                          : "Last modified --"}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="hub-empty">No idea books yet.</div>
                )}
              </div>
            </Paper>
          </div>

          <div className="hub-main-right">
            <Paper className="hub-card hub-designer-card" elevation={0}>
              {hasActiveCollaboration ? (
                <>
                  <div className="hub-card-header">
                    <Typography variant="h6" fontWeight={700}>
                      My Designer
                    </Typography>
                  </div>
                  <div className="hub-designer-profile">
                    <Avatar
                      src={activeDesigner?.profilePicUrl || undefined}
                      className="hub-designer-avatar"
                    >
                      {(activeDesigner?.name || "D").charAt(0)}
                    </Avatar>
                    <div>
                      <div className="hub-designer-name">
                        {activeDesigner?.name || "Your Designer"}
                      </div>
                      <div className="hub-designer-rating">
                        {activeDesignerRating ? (
                          <span className="hub-designer-score">
                            {activeDesignerRating.toFixed(1)}
                          </span>
                        ) : null}
                        <Rating
                          value={activeDesignerRating ?? 0}
                          precision={0.5}
                          readOnly
                          size="small"
                        />
                        <span className="hub-designer-rating-label">Compatibility Rating</span>
                      </div>
                    </div>
                  </div>
                  <div className="hub-designer-actions">
                    <Button
                      variant="outlined"
                      className="hub-button-outline"
                      onClick={() =>
                        handleViewDesignerProfile(activeDesigner?._id, activeMatch)
                      }
                    >
                      View Profile
                    </Button>
                    <Button
                      variant="contained"
                      className="hub-button-primary"
                      onClick={handleNavigateMessages}
                    >
                      Message Me
                    </Button>
                  </div>
                  <Button
                    variant="text"
                    className="hub-end-link"
                    onClick={() => setEndDialogOpen(true)}
                  >
                    End Collaboration →
                  </Button>
                  {activeCollaboration?.collaboration?.status === "end_requested" && (
                    <div className="hub-muted">End request pending confirmation.</div>
                  )}
                </>
              ) : (
                <>
                  <div className="hub-card-header">
                    <Typography variant="h6" fontWeight={700}>
                      My Top Matches
                    </Typography>
                  </div>
                  <div className="hub-top-matches">
                    {topMatches.length ? (
                      topMatches.map((match) => {
                        const rating = getRatingFromScore(match.matchScore ?? null);
                        const designerIdLabel = getMatchDesignerIdLabel(match);
                        return (
                          <div key={match.matchId} className="hub-top-match-card">
                            <div className="hub-top-match-info">
                              <Avatar src={match.profilePicUrl || undefined}>
                                {designerIdLabel.charAt(0)}
                              </Avatar>
                              <div>
                                <div className="hub-top-match-name">
                                  {designerIdLabel}
                                </div>
                                <div className="hub-top-match-rating">
                                  <span className="hub-top-match-score">
                                    {rating ? rating.toFixed(1) : "--"}
                                  </span>
                                  <Rating
                                    value={rating ?? 0}
                                    precision={0.5}
                                    readOnly
                                    size="small"
                                  />
                                  <span className="hub-top-match-rating-label">
                                    Compatibility Rating
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="hub-top-match-actions">
                              <Button
                                variant="outlined"
                                className="hub-button-outline"
                                onClick={handleNavigateMessages}
                              >
                                Message Me
                              </Button>
                              <Button
                                variant="contained"
                                className="hub-button-primary"
                                onClick={() =>
                                  handleViewDesignerProfile(
                                    match.profileId ?? match.matchId,
                                    match
                                  )
                                }
                              >
                                View Profile
                              </Button>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <div className="hub-empty">No matches yet.</div>
                    )}
                  </div>
                </>
              )}
            </Paper>

            <Paper className="hub-card hub-activity-card" elevation={0}>
              <div className="hub-card-header">
                <Typography variant="h6" fontWeight={700}>
                  Activity Feed
                </Typography>
              </div>
              <div className="hub-activity-list">
                {activityItems.map((item) => (
                  <div key={item.id} className="hub-activity-item">
                    <Avatar className="hub-activity-avatar" src={item.avatarUrl ?? undefined}>
                      {(item.name || "A").charAt(0)}
                    </Avatar>
                    <div>
                      <div className="hub-activity-name">{item.name}</div>
                      <div className="hub-activity-text">{item.text}</div>
                    </div>
                  </div>
                ))}
              </div>
            </Paper>
          </div>
        </div>

        <Dialog
          open={projectDialogOpen}
          onClose={handleCloseProjectDialog}
          maxWidth="lg"
          fullWidth
          className="hub-project-dialog"
        >
          <DialogTitle className="hub-project-dialog-title">
            <span>
              {projectDialogMode === "add" ? "Add New Project" : "Review/Edit Project"}
            </span>
            <IconButton onClick={handleCloseProjectDialog} size="small">
              <CloseRoundedIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent className="hub-project-dialog-content">
            <HomeownerProjectCreatePage
              embedded
              onComplete={handleProjectDialogComplete}
              onClose={handleCloseProjectDialog}
              projectIdOverride={
                projectDialogMode === "edit" ? primaryProject?._id ?? null : null
              }
            />
          </DialogContent>
        </Dialog>

        <Dialog
          open={endDialogOpen}
          onClose={() => setEndDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            End Collaboration{activeDesigner?.name ? ` with ${activeDesigner.name}` : ""}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" className="hub-muted" mb={2}>
              You are about to request to end this collaboration. You will need to restart your
              designer matching profile to receive new matches.
            </Typography>
            <TextField
              label="Add a note (optional)"
              multiline
              minRows={4}
              fullWidth
              value={endNote}
              onChange={(event) => setEndNote(event.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEndDialogOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={handleRequestEndCollaboration}
              disabled={requestingEnd}
            >
              {requestingEnd ? "Submitting..." : "Confirm & End"}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={feedbackDialogOpen}
          onClose={() => setFeedbackDialogOpen(false)}
          maxWidth="sm"
          fullWidth
          className="hub-feedback-dialog"
        >
          <DialogTitle className="hub-feedback-title">
            <span>Mark Project as Complete</span>
            <IconButton
              size="small"
              onClick={() => setFeedbackDialogOpen(false)}
              aria-label="Close"
            >
              <CloseRoundedIcon />
            </IconButton>
          </DialogTitle>
          <div className="hub-feedback-divider" />
          <DialogContent className="hub-feedback-content">
            <div className="hub-feedback-question-row">
              <Typography className="hub-feedback-question">
                How would you rate your recent project collaboration with{" "}
                <span className="hub-feedback-name">{feedbackDesignerName}</span>
              </Typography>
              <Avatar
                className="hub-feedback-avatar"
                src={activeDesigner?.profilePicUrl ?? undefined}
              >
                {feedbackDesignerName.charAt(0)}
              </Avatar>
            </div>

            <div className="hub-feedback-rating-block">
              <div className="hub-feedback-rating-label">Overall Rating</div>
              <Rating
                value={feedbackRating ?? 0}
                precision={0.5}
                onChange={(_, value) => setFeedbackRating(value)}
                size="large"
                className="hub-feedback-rating"
              />
              <div className="hub-feedback-message">{feedbackMessage}</div>
            </div>

            {isPositiveFeedback ? (
              <div className="hub-feedback-field">
                <TextField
                  placeholder="Share your review here."
                  multiline
                  minRows={5}
                  fullWidth
                  value={feedbackText}
                  onChange={(event) => setFeedbackText(event.target.value)}
                  inputProps={{ maxLength: 500 }}
                  className="hub-feedback-textarea"
                />
                <div className="hub-feedback-count">{feedbackText.length}/500</div>
              </div>
            ) : (
              <>
                <div className="hub-feedback-label">
                  What could {feedbackDesignerName} have done better?
                </div>
                <div className="hub-feedback-field">
                  <TextField
                    placeholder="Share your feedback here."
                    multiline
                    minRows={5}
                    fullWidth
                    value={feedbackText}
                    onChange={(event) => setFeedbackText(event.target.value)}
                    inputProps={{ maxLength: 500 }}
                    className="hub-feedback-textarea"
                  />
                  <div className="hub-feedback-count">{feedbackText.length}/500</div>
                </div>
                <div className="hub-feedback-label">
                  How could Home Design Guild have better supported you?
                </div>
                <div className="hub-feedback-field">
                  <TextField
                    placeholder="Share your feedback here."
                    multiline
                    minRows={5}
                    fullWidth
                    value={platformFeedback}
                    onChange={(event) => setPlatformFeedback(event.target.value)}
                    inputProps={{ maxLength: 500 }}
                    className="hub-feedback-textarea"
                  />
                  <div className="hub-feedback-count">{platformFeedback.length}/500</div>
                </div>
              </>
            )}

            <div className="hub-feedback-actions">
              <Button
                className="hub-feedback-cancel"
                onClick={() => setFeedbackDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                className="hub-feedback-submit"
                onClick={handleSubmitFeedback}
                disabled={submittingFeedback}
              >
                {submittingFeedback ? "Submitting..." : feedbackSubmitLabel}
              </Button>
            </div>

            <div className="hub-feedback-footer">
              By submitting this review, you agree to our{" "}
              <a href={Pages.TERMS_OF_SERVICE}>Terms</a> and{" "}
              <a href={Pages.PRIVACY_POLICY}>Privacy Policy</a>
            </div>
          </DialogContent>
        </Dialog>
      </Box>
    );
  }

  // For paid plan designers with complete onboarding, show the designer hub.
  return (
    <Box className="designer-hub">
      <DashboardHeader
        title="Dashboard"
        showBackButton={false}
        primaryAction={{
          label: "Refresh",
          onClick: handleManualRefresh,
          variant: "outlined",
        }}
      />

      {shouldShowEndRequestBanner && (
        <div className="designer-end-banner">
          <div className="designer-end-banner-content">
            <Avatar className="designer-end-banner-avatar">
              {endRequestHomeownerName.charAt(0)}
            </Avatar>
            <div className="designer-end-banner-text">
              <div className="designer-end-banner-title">
                <span className="designer-end-banner-name">{endRequestHomeownerName}</span> has
                requested to end your collaboration.
              </div>
              <div className="designer-end-banner-subtitle">
                They&apos;ve shared a note about why they&apos;d like to end the project. Please
                review and confirm to complete the process.
              </div>
            </div>
          </div>
          <Button
            variant="outlined"
            className="designer-end-banner-button"
            endIcon={<ChevronRightIcon />}
            onClick={() => setReviewEndDialogOpen(true)}
          >
            Review Request
          </Button>
        </div>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={12} lg={7}>
          <Grid container spacing={2}>
            {designerMetrics.map((metric, idx) => (
              <Grid
                key={idx}
                item
                xs={12}
                sm={6}
                md={metric.wide ? 6 : 3}
                lg={metric.wide ? 12 : 6}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    height: "100%",
                    backgroundColor: "#fff",
                    boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.06)",
                    "&:hover": {
                      boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
                    },
                  }}
                >
                  <Stack display="flex" spacing={1} alignItems="center" textAlign="center">
                    <Box color="#d22268" fontSize="1.5rem">
                      {metric.icon}
                    </Box>
                    <Typography variant="subtitle2" fontWeight={500} color="text.secondary">
                      {metric.label}
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="#00a06d">
                      {metric.rating ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          {typeof metric.value === "number"
                            ? metric.value.toFixed(1)
                            : "--"}
                          <Rating
                            value={typeof metric.value === "number" ? metric.value : 0}
                            precision={0.5}
                            readOnly
                            size="small"
                            sx={{ color: "#ffc107" }}
                          />
                        </Box>
                      ) : (
                        String(metric.value ?? 0).padStart(2, "0")
                      )}
                    </Typography>
                    {metric.button && (
                      <Button
                        className="your-hub-button"
                        variant="outlined"
                        size="small"
                        onClick={metric.onClick}
                        sx={{
                          textTransform: "none",
                          fontWeight: 600,
                          color: "#d22268",
                          borderColor: "#d22268",
                          "&:hover": {
                            borderColor: "#d22268",
                            backgroundColor: "rgba(210, 34, 104, 0.04)",
                          },
                        }}
                      >
                        {metric.button}
                      </Button>
                    )}
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Grid>

        <Grid item xs={12} md={12} lg={5}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 4,
              border: "1px solid #e5e7eb",
              backgroundColor: "#fff",
              height: "100%",
            }}
          >
            <Typography variant="h6" fontWeight={700} mb={2}>
              Recent Matches
            </Typography>
            <Stack spacing={2}>
              {!designerRecentMatches.length && (
                <Box sx={{ color: "text.secondary" }}>No matches yet.</Box>
              )}
              {designerRecentMatches.map((match) => (
                <Box
                  key={match.id}
                  sx={{
                    p: 2,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderRadius: 2,
                    backgroundColor: "#F5F5F5",
                    border: "1px solid rgb(212, 212, 212)",
                    "&:hover": {
                      backgroundColor: "#EAF0FC",
                    },
                    height: "100%",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={2}>
                    <Avatar src={match.avatar} sx={{ width: 36, height: 36 }}>
                      {(match.name || match.id).charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {match.name || match.id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {match.category}
                      </Typography>
                    </Box>
                  </Box>
                  <Box textAlign="right">
                    <Typography
                      variant="caption"
                      color={match.status === "Online" ? "#00a06d" : "#B71C1C"}
                      fontWeight={600}
                      fontSize={14}
                      sx={{ display: "block", mb: 1, fontWeight: 600 }}
                    >
                      <Box component="span" key={match.id} fontWeight={300} fontSize={20}>
                        ● &nbsp;
                      </Box>
                      <Box component="span" color="#1C1C1C" fontWeight={300}>
                        {match.status}
                      </Box>
                    </Typography>
                    <Button
                      className="your-hub-button"
                      variant="outlined"
                      size="small"
                      onClick={handleNavigateMessages}
                      sx={{
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: 14,
                        color: "#d22268",
                        borderColor: "#d22268",
                        borderRadius: 2,
                        "&:hover": {
                          borderColor: "#d22268",
                          backgroundColor: "rgba(210, 34, 104, 0.04)",
                        },
                      }}
                    >
                      Chat Now
                    </Button>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      <Paper className="designer-projects-card" elevation={0}>
        <div className="designer-projects-header">
          <Typography variant="h6" fontWeight={700}>
            My Projects
          </Typography>
        </div>
        <div className="designer-projects-table">
          <div className="designer-projects-row designer-projects-row--head">
            <span>Client</span>
            <span>Date Matched</span>
            <span>Status</span>
            <span>Actions</span>
          </div>
          {loadingDesignerProjects && (
            <div className="designer-projects-row designer-projects-row--empty">
              Loading projects...
            </div>
          )}
          {!loadingDesignerProjects && !designerProjects.length && (
            <div className="designer-projects-row designer-projects-row--empty">
              No projects yet.
            </div>
          )}
          {!loadingDesignerProjects &&
            designerProjects.map((project) => {
              const matchedAtLabel = project.matchedAt
                ? formatShortDate(project.matchedAt)
                : "—";
              const completionPending =
                Boolean(project.completionRequestedBy) &&
                !project.completionConfirmedAt &&
                project.status !== "completed";
              const pendingByDesigner = project.completionRequestedBy === "designer";
              const pendingByHomeowner =
                completionPending && project.completionRequestedBy === "homeowner";
              const statusLabel = pendingByHomeowner
                ? "Homeowner requested complete project"
                : getDesignerStatusLabel(project.status);
              const canStart = project.status === "not_started" && !completionPending;
              const canMarkComplete =
                project.status === "in_progress" && !completionPending;
              return (
                <div key={`${project.projectId}-${project.homeownerId}`} className="designer-projects-row">
                  <div className="designer-projects-client">
                    <Avatar src={project.homeownerAvatarUrl || undefined}>
                      {(project.homeownerName || "H").charAt(0)}
                    </Avatar>
                    <div>
                      <div className="designer-projects-name">
                        {project.homeownerName || "Homeowner"}
                      </div>
                      {project.projectName ? (
                        <div className="designer-projects-sub">{project.projectName}</div>
                      ) : null}
                    </div>
                  </div>
                  <div className="designer-projects-date">{matchedAtLabel}</div>
                  <div className={`designer-projects-status ${project.status}`}>
                    <span className="designer-projects-dot" />
                    {statusLabel}
                  </div>
                  <div className="designer-projects-actions">
                    {completionPending && pendingByDesigner ? (
                      <Button
                        size="small"
                        variant="outlined"
                        className="designer-projects-action"
                        disabled
                      >
                        Pending Approval
                      </Button>
                    ) : completionPending && pendingByHomeowner ? (
                      <Button
                        size="small"
                        variant="outlined"
                        className="designer-projects-action"
                        onClick={() => handleDesignerConfirmCompletion(project.projectId)}
                        disabled={updatingDesignerProjectId === project.projectId}
                      >
                        {updatingDesignerProjectId === project.projectId
                          ? "Updating..."
                          : "Mark as Complete"}
                      </Button>
                    ) : canStart ? (
                      <Button
                        size="small"
                        variant="outlined"
                        className="designer-projects-action"
                        onClick={() => handleDesignerStartProject(project.projectId)}
                        disabled={updatingDesignerProjectId === project.projectId}
                      >
                        {updatingDesignerProjectId === project.projectId
                          ? "Updating..."
                          : "Start Project"}
                      </Button>
                    ) : canMarkComplete ? (
                      <Button
                        size="small"
                        variant="outlined"
                        className="designer-projects-action"
                        onClick={() => handleDesignerRequestCompletion(project.projectId)}
                        disabled={updatingDesignerProjectId === project.projectId}
                      >
                        {updatingDesignerProjectId === project.projectId
                          ? "Updating..."
                          : "Mark as Complete"}
                      </Button>
                    ) : (
                      <span className="designer-projects-action-muted">—</span>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </Paper>

      <Dialog
        open={reviewEndDialogOpen}
        onClose={() => setReviewEndDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        className="designer-end-dialog"
      >
        <DialogTitle className="designer-end-dialog-title">
          <span>End Collaboration with {endRequestHomeownerName}</span>
          <IconButton size="small" onClick={() => setReviewEndDialogOpen(false)}>
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <div className="designer-end-dialog-divider" />
        <DialogContent className="designer-end-dialog-content">
          <Typography className="designer-end-dialog-text">
            You&apos;re reviewing a request from{" "}
            <span className="designer-end-dialog-name">{endRequestHomeownerName}</span> to end your
            collaboration. This will pause communication and remove this project from active
            status. You&apos;ll need to restart your matching profile to receive new matches.
          </Typography>
          <div className="designer-end-dialog-label">Notes:</div>
          <div className="designer-end-dialog-note">{endRequestNote}</div>
          <div className="designer-end-dialog-actions">
            <Button
              className="designer-end-dialog-cancel"
              onClick={() => setReviewEndDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              className="designer-end-dialog-confirm"
              variant="contained"
              onClick={handleConfirmEndCollaboration}
              disabled={confirmingEnd}
            >
              {confirmingEnd ? "Ending..." : "Confirm & End"}
            </Button>
          </div>
          <div className="designer-end-dialog-footer">
            Need help? Contact{" "}
            <a href={Pages.CONTACT_US}>Home Design Guild Support</a>
          </div>
        </DialogContent>
      </Dialog>
    </Box>
  );
};
